import * as React from "react";
import {useEffect} from "react";
import {Box, Button, Fab, Grid, Paper, TextField, Typography} from "@mui/material";
import {NavLink, useNavigate} from "react-router-dom";
import {Add, Search} from "@mui/icons-material";
import {toast} from "react-toastify";
import {Field, Form, Formik} from "formik";
import {BaseService} from "../../../services/base-service";
import {URLS} from "../../../services/app-urls";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {translate} from "../../../translate/translate";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {ErrorComponent} from "../../../components/error/error.component";
import {useKeepFilters} from "../../../utils/form-utils";
import {Customer} from "../../../models/general/customer";
import Loading from "../../../components/loading/loading";

const CustomerComponent = () => {
    const navigate = useNavigate();
    const service = new BaseService<Customer>(URLS.CUSTOMER);
    const [dataSource, setDataSource] = React.useState<Customer[] | []>([]);
    const [pagination, setPagination] = React.useState<PaginatedResult<Customer>>();
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [page, setPage] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);
    const url = "general/customer/";

    const {searchParams, setFilterParameter} = useKeepFilters();
    const searchName = searchParams.get("name") || "";

    const search = () => {
        setIsLoading(true);
        if (searchName) {
            service.clearParameters();
            service.addParameter("name", searchName);
            service.addParameter("limit", rowsPerPage);
            service.addParameter("offset", (page * rowsPerPage));
            service.getAllPaginate().then(response => {
                setPagination(response.data);
            }).catch((error: any) => {
                ErrorComponent(error);
            }).finally(() => {
                setIsLoading(false);
            });
        } else {
            service.clearParameters();
            service.addParameter("limit", rowsPerPage);
            service.addParameter("offset", (page * rowsPerPage));
            service.getAllPaginate().then(response => {
                setPagination(response.data);
            }).catch((error: any) => {
                ErrorComponent(error);
            }).finally(() => {
                setIsLoading(false);
            });
        }
    };

    useEffect(() => {
        setDataSource(pagination?.results ?? []);
    }, [pagination]);

    useEffect(() => {
        search();
    }, [page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDoubleClick = (row: Customer) => {
        navigate(`${row.id}?${searchParams.toString()}`);
    };


    const handleDelete = (row: Customer) => {
        service.delete(Number(row.id)).then(() => {
            toast.success(translate.t("successfully_deleted"));
            service.clearParameters();
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    return (
        <section>
            <Loading open={isLoading} />
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("customer")}
                </Typography>
            </div>
            <div className="floating-button">
                <Button
                    component={NavLink}
                    to={"create/"}
                    startIcon={<Add/>}
                    variant="contained"
                    color="primary">
                    {translate.t("new")}
                </Button>
            </div>

            <Grid item xs={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                if (page === 0) {
                                  search();
                                } else {
                                  setPage(0);
                                }
                                setSubmitting(false);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Grid alignItems={"center"} container spacing={2} direction={"row"}>
                                            <Grid item xs>
                                                <Field
                                                    as={TextField}
                                                    name="name"
                                                    label={translate.t("name")}
                                                    fullWidth
                                                    variant={"outlined"}
                                                    autoFocus={true}
                                                    value={searchName}
                                                    onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                                                        setFilterParameter("name", event.target.value as string)}/>
                                            </Grid>
                                            <Grid item>
                                                <Fab
                                                    size="small"
                                                    type="submit"
                                                    color="primary">
                                                    <Search/>
                                                </Fab>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["#", "name", "action"]}
                    showActions={["edit", "delete"]}
                    displayedColumns={["id", "name", "action"]}
                    handleDelete={handleDelete}
                    url={url}
                    handleDoubleClick={handleDoubleClick}
                    count={pagination?.count}
                    next={pagination?.next}
                    previous={pagination?.previous}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    columnStyles={{1:"5%", 2:"95%",}}
                />
            </Paper>
        </section>
    );

};

export default CustomerComponent;
